import { useIntl } from 'react-intl'
import Table from 'LuxKit/Tables/Table'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import getPropertyIcon from 'lib/offer/getPropertyIcon'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import type { ExtraGuestPolicyItem, IconTextItem } from './AccommodationAmenitiesAndPoliciesContent'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'

interface Props {
  extraGuestGeneratedPolicyItems: Array<ExtraGuestPolicyItem>
}

function resolveIcon(iconName: string | undefined) {
  if (!iconName) return undefined
  if (iconName.startsWith('line_') || iconName.startsWith('solid_')) {
    return getPropertyIcon(iconName as App.Property.IconName)
  }
  return getOfferFeatureIcon(iconName as App.OfferFeatureSymbol)
}

const getGuestTypeKey = (guestType: ExtraGuestPolicyItem) => {
  return `${guestType.guest_type_label}-${guestType.age_range}`
}

function InclusionItem({ item }: { item: IconTextItem }) {
  const Icon = resolveIcon(item.icon)
  if (Icon) {
    return <BodyTextBlock variant="medium" startIcon={<Icon aria-hidden="true" />}>{item.text}</BodyTextBlock>
  }
  return <BodyText variant="medium">{item.text}</BodyText>
}

const renderGuestTypeRow = (guestType: ExtraGuestPolicyItem) => {
  const {
    guest_type_label,
    age_range,
    fees: {
      guest_price_per_night,
      fee_payable_to,
    },
    inclusions,
  } = guestType

  return (
    <TableBodyRow key={getGuestTypeKey(guestType)}>
      <TableBodyCell>
        <BodyText variant="medium">{guest_type_label}</BodyText>
        <BodyText variant="medium">{age_range}</BodyText>
      </TableBodyCell>
      <TableBodyCell>
        <BodyText variant="medium" weight="bold">{guest_price_per_night}</BodyText>
        <BodyText variant="medium">{fee_payable_to}</BodyText>
      </TableBodyCell>
      <TableBodyCell>
        {inclusions.map((inc) => <InclusionItem key={inc.text} item={inc} />)}
      </TableBodyCell>
    </TableBodyRow>
  )
}

function AccommodationExtraGuestPoliciesTable({ extraGuestGeneratedPolicyItems }: Props) {
  const intl = useIntl()

  const heading = intl.formatMessage({
    id: 'IiSn78A7-zYbBBSxjGYg',
    defaultMessage: 'Extra guest policy',
    description: 'Heading for the extra guest policy table within the guest policy area of the room details modal.',
  })

  const guestsHeader = intl.formatMessage({
    id: 'C6p-PA9Iroqx8GCuO0l4',
    defaultMessage: 'Guests',
    description: 'Table column header for guest type in the extra guest policy table of the room details modal.',
  })

  const feesHeader = intl.formatMessage({
    id: 'q2V3VBZ4OcWDrR9ZaLUc',
    defaultMessage: 'Fees',
    description: 'Table column header for fee information in the extra guest policy table of the room details modal.',
  })

  const inclusionsHeader = intl.formatMessage({
    id: 'ogOHaKSZ-633V2YXmqKC',
    defaultMessage: 'Inclusions',
    description: 'Table column header for included amenities in the extra guest policy table of the room details modal.',
  })

  return (
    <>
      <BodyText variant="medium" weight="bold" as="h4">{heading}</BodyText>
      <Table>
        <TableHeader>
          <TableHeaderRow>
            <TableHeaderCell>{guestsHeader}</TableHeaderCell>
            <TableHeaderCell>{feesHeader}</TableHeaderCell>
            <TableHeaderCell>{inclusionsHeader}</TableHeaderCell>
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {extraGuestGeneratedPolicyItems.map(renderGuestTypeRow)}
        </TableBody>
      </Table>
    </>
  )
}

export default AccommodationExtraGuestPoliciesTable
